import type { Ticket } from '@/lib/mock';
import { ThemeToggle } from '@/components/theme-toggle';
import { Search, Plus, Filter, Mail } from 'lucide-react';
import { useAuth } from '@/lib/auth';

import { useState, useRef, useEffect, useMemo } from 'react';

const STATES = ['Open', 'Assigned', 'In Progress', 'Pending', 'On Hold', 'Resolved', 'Closed'];
const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

export function UserTicketList({ tickets, isServiceRequest = false, onSelectTicket, onNewTicket }: { tickets: Ticket[], isServiceRequest?: boolean, onSelectTicket: (id: string) => void, onNewTicket: () => void }) {
  const { user, session } = useAuth();
  const currentUserId = user?.id || session?.user?.id;
  const currentUserName = (user?.displayName || user?.name || session?.user?.name || '').toLowerCase().trim();
  const currentUserEmail = (user?.email || session?.user?.email || '').toLowerCase().trim();
  const [searchQuery, setSearchQuery] = useState('');
  
  const [filters, setFilters] = useState({
    ref: '',
    subject: '',
    category: '',
    impact: '',
    urgency: '',
    location: '',
    assignee: '',
    state: '',
    priority: ''
  });

  const myTickets = useMemo(() => {
    return tickets.filter(t => {
      if (currentUserId && (t.requesterId === currentUserId || (t as any).createdBy === currentUserId)) return true;
      if (currentUserEmail && (
        (t.requesterEmail && t.requesterEmail.toLowerCase().trim() === currentUserEmail) ||
        (t.requester && t.requester.toLowerCase().trim() === currentUserEmail)
      )) return true;
      if (currentUserName && t.requester && t.requester.toLowerCase().trim() === currentUserName) return true;
      if (!t.requesterId && !t.requesterEmail && (!t.requester || t.requester === '—')) return true;
      return false;
    });
  }, [tickets, currentUserId, currentUserEmail, currentUserName]);
  
  const filteredTickets = useMemo(() => {
    return myTickets.filter(t => {
      if (isServiceRequest ? t.category !== 'Service Request' : t.category === 'Service Request') return false;
      
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = (
          t.ref?.toLowerCase().includes(q) ||
          t.subject?.toLowerCase().includes(q) ||
          t.category?.toLowerCase().includes(q) ||
          t.impact?.toLowerCase().includes(q) ||
          t.urgency?.toLowerCase().includes(q) ||
          t.location?.toLowerCase().includes(q) ||
          t.assignee?.toLowerCase().includes(q) ||
          t.state?.toLowerCase().includes(q) ||
          t.priority?.toLowerCase().includes(q)
        );
        if (!match) return false;
      }
      
      if (filters.ref && !t.ref?.toLowerCase().includes(filters.ref.toLowerCase())) return false;
      if (filters.subject && !t.subject?.toLowerCase().includes(filters.subject.toLowerCase())) return false;
      if (filters.category && t.category !== filters.category) return false;
      if (filters.impact && t.impact !== filters.impact) return false;
      if (filters.urgency && t.urgency !== filters.urgency) return false;
      if (filters.location && !t.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (filters.assignee && !t.assignee?.toLowerCase().includes(filters.assignee.toLowerCase())) return false;
      if (filters.state && t.state !== filters.state) return false;
      if (filters.priority && t.priority !== filters.priority) return false;
      
      return true;
    }).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [myTickets, isServiceRequest, searchQuery, filters]);

  return (
    <div className="flex h-full flex-col bg-theme p-6 overflow-hidden">
      <div className="mx-auto max-w-5xl w-full flex flex-col h-full">
        
        <div className="flex justify-between items-center mb-6 shrink-0">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {isServiceRequest ? 'Service Requests' : 'My Tickets'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isServiceRequest ? 'Track the status of your hardware and software requests.' : 'View and track your active support tickets.'}
            </p>
          </div>
          <ThemeToggle />
        </div>

        <div className="neo rounded-2xl overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="p-4 border-b border-border flex justify-between items-center bg-muted/20 shrink-0">
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full rounded-[1rem] neo-inset pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50"
              />
            </div>
            <button onClick={onNewTicket} className="flex items-center gap-2 h-9 px-4 rounded-[1rem] neo-interactive bg-[var(--color-primary)] text-[var(--color-primary-content)] text-sm font-bold hover:brightness-110 transition-all">
              <Plus className="h-4 w-4" />
              {isServiceRequest ? 'New Request' : 'New Ticket'}
            </button>
          </div>
          
          <div className="flex-1 overflow-auto relative">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-muted/40 sticky top-0 z-10 border-b border-border">
                <tr>
                  <th className="px-6 py-3.5 font-semibold text-muted-foreground w-44 min-w-[170px] text-left group relative">
                    <div className="flex items-center gap-2">
                      <span>Reference</span>
                      <FilterDropdown filterValue={filters.ref} setFilter={(val) => setFilters(f => ({ ...f, ref: val }))} type="text" />
                    </div>
                  </th>
                  <th className="px-6 py-3.5 font-medium text-muted-foreground group relative min-w-[200px]">
                    <div className="flex items-center gap-1.5">
                      Subject
                      <FilterDropdown filterValue={filters.subject} setFilter={(val) => setFilters(f => ({ ...f, subject: val }))} type="text" />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Category
                      <FilterDropdown filterValue={filters.category} setFilter={(val) => setFilters(f => ({ ...f, category: val }))} type="select" options={['Hardware', 'Software', 'Network', 'Access', 'Security', 'Other']} />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Impact
                      <FilterDropdown filterValue={filters.impact} setFilter={(val) => setFilters(f => ({ ...f, impact: val }))} type="select" options={['High', 'Medium', 'Low']} />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Urgency
                      <FilterDropdown filterValue={filters.urgency} setFilter={(val) => setFilters(f => ({ ...f, urgency: val }))} type="select" options={['High', 'Medium', 'Low']} />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Location
                      <FilterDropdown filterValue={filters.location} setFilter={(val) => setFilters(f => ({ ...f, location: val }))} type="text" />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Assignee
                      <FilterDropdown filterValue={filters.assignee} setFilter={(val) => setFilters(f => ({ ...f, assignee: val }))} type="text" />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Status
                      <FilterDropdown filterValue={filters.state} setFilter={(val) => setFilters(f => ({ ...f, state: val }))} type="select" options={STATES} />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground group relative">
                    <div className="flex items-center gap-1.5">
                      Priority
                      <FilterDropdown filterValue={filters.priority} setFilter={(val) => setFilters(f => ({ ...f, priority: val }))} type="select" options={PRIORITIES} />
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium text-muted-foreground">Due</th>
                  <th className="px-6 py-3 font-medium text-muted-foreground">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-muted-foreground">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map(t => (
                    <tr key={t.id} onClick={() => onSelectTicket(t.id)} className="hover:bg-muted/30 transition cursor-pointer">
                      <td className="px-6 py-4 font-mono font-semibold text-foreground w-44 min-w-[170px] text-left align-middle whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-primary tracking-tight">{t.ref}</span>
                          {t.source === 'Email' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-sans font-medium bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/25">
                              <Mail className="w-3 h-3" />
                              Email
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 truncate max-w-xs">{t.subject}</td>
                      <td className="px-6 py-4 text-muted-foreground">{t.category || '—'}</td>
                      <td className="px-6 py-4 text-muted-foreground">{t.impact || '—'}</td>
                      <td className="px-6 py-4 text-muted-foreground">{t.urgency || '—'}</td>
                      <td className="px-6 py-4 text-muted-foreground">{t.location || '—'}</td>
                      <td className="px-6 py-4 text-muted-foreground">{t.assignee || 'Unassigned'}</td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 bg-muted text-foreground text-xs rounded-full font-medium">
                          {t.state}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
                          {t.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {t.dueAt ? new Date(t.dueAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {new Date(t.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

function FilterDropdown({ filterValue, setFilter, type, options }: { filterValue: string, setFilter: (v: string) => void, type: 'text' | 'select', options?: string[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref]);

  return (
    <div className="relative inline-flex" ref={ref}>
      <button 
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`p-1 rounded transition hover:bg-muted ${filterValue ? 'text-primary bg-primary/10' : 'text-muted-foreground/50'}`}
      >
        <Filter className="w-3.5 h-3.5" />
      </button>
      
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 neo rounded-[1rem] p-2 z-50">
          <div className="text-xs font-semibold mb-2 text-foreground">Filter by</div>
          {type === 'text' ? (
            <input 
              autoFocus
              type="text" 
              placeholder="Contains..."
              value={filterValue} 
              onChange={e => setFilter(e.target.value)} 
              className="w-full h-8 text-sm px-2 rounded-lg neo-inset font-normal outline-none focus:ring-1 focus:ring-[var(--color-primary)]/50" 
            />
          ) : (
            <select 
              autoFocus
              value={filterValue} 
              onChange={e => setFilter(e.target.value)} 
              className="w-full h-8 text-sm px-2 rounded-lg neo-inset font-normal outline-none focus:ring-1 focus:ring-[var(--color-primary)]/50"
            >
              <option value="">All</option>
              {options?.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          <div className="flex justify-end mt-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setFilter(''); setOpen(false); }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
